import React, { useState, useEffect, useRef } from 'react';
import { Signal, SignalLabel } from '@catalyst-iq/shared';
import { TopLosersSection } from './TopLosersSection';
import { api } from '../services/api';
import { TopGainersSection } from './TopGainersSection';
import { useOutletContext, useLocation } from 'react-router-dom';
import { FilterBar } from './filterBar';
import { SignalsTable } from './signalsTable';

// Sticky header (56/60) + market ticker (~36) + small gap — keep in sync with RootLayout
const SECTION_SCROLL_IDS = new Set(['gainers', 'losers']);

function scrollToSection(id: string, behavior: ScrollBehavior = 'smooth') {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior, block: 'start' });
}

// Main scanner page: fetches signals on a polling interval and renders the filter bar, table, and top movers sections
export function ScannerPage() {
  // 1. שימוש ב-setGlobalCounts מתוך ה-Context של הלייאאוט הראשי

  const location = useLocation();
  const { setCounts: setGlobalCounts } = useOutletContext<{ 
    setCounts: React.Dispatch<React.SetStateAction<{ all: number; catalyst: number; momentum: number; none: number; gainers: number; losers: number; watchlist: number }>> 
  }>();

  const [signals, setSignals] = useState<Signal[]>([]);
  const [filter, setFilter]   = useState('all');
  const [sort, setSort]       = useState('detected');
  const [loading, setLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [gainersScanning, setGainersScanning] = useState(false);
  const [losersScanning, setLosersScanning] = useState(false);
  const [scanPulse, setScanPulse] = useState(0);
  const [error, setError]     = useState<string | null>(null);
  const [retryTrigger, setRetryTrigger] = useState(0);

  // Any section scanning → all tables + fixed viewport overlay (visible wherever you scrolled)
  const anyScanning = isScanning || gainersScanning || losersScanning;
  //const [activeToasts, setActiveToasts] = useState<ScannerAlert[]>([]);

  //
  
  // 2. תיקון חיוני: שינוי שם פונקציית העדכון המקומית ל-setLocalCounts כדי למנוע התנגשות
  const [counts, setLocalCounts] = useState({
    all: 0,
    catalyst: 0,
    momentum: 0,
    rebound: 0,
    none: 0
    
  });

  const scrollPositionRef = useRef<number>(0);
  const currentDelay = useRef(30000);

  useEffect(() => {
    if (!loading && signals.length > 0) {
      const hashId = location.hash.replace('#', '');
      // Don't fight hash navigation to Top Gainers / Top Losers
      if (SECTION_SCROLL_IDS.has(hashId)) return;
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [signals, loading, location.hash]);

  // After content is ready, land on the section title (scroll-margin clears the sticky chrome)
  useEffect(() => {
    if (loading) return;
    const hashId = location.hash.replace('#', '');
    if (!SECTION_SCROLL_IDS.has(hashId)) return;
    const frame = requestAnimationFrame(() => scrollToSection(hashId));
    return () => cancelAnimationFrame(frame);
  }, [loading, location.hash]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    let scanHoldId: NodeJS.Timeout;

    const fetchData = async (isBackground = false) => {
      if (!isBackground && signals.length === 0) {
        setLoading(true);
      }
      // Background refreshes drive the scan visual; keep it on briefly so the sweep is readable
      const scanStartedAt = Date.now();
      const showScan = isBackground || signals.length > 0;
      if (showScan) {
        setIsScanning(true);
      }
      setError(null);
      const labelParam = filter !== 'all' ? (filter as SignalLabel) : undefined;   
      scrollPositionRef.current = window.scrollY;     
      try {
        const data = await api.getSignals({ label: labelParam, sort, limit: 100 });
        if (!isMounted) return;

        const list = data.signals || [];
        setSignals(list);

        // Counts from the rows currently shown in the table
        const fromRows = {
          all: list.length,
          catalyst: list.filter((s) => s.label === 'catalyst').length,
          momentum: list.filter((s) => s.label === 'momentum').length,
          rebound: list.filter((s) => s.label === 'rebound').length,
          none: list.filter((s) => s.label === 'none').length,
        };

        // When a label filter is active the table only has that slice —
        // keep filter-tab totals from API stats so other tabs stay accurate.
        const forFilterBar =
          filter === 'all' || !data.stats
            ? fromRows
            : {
                all: data.stats.total,
                catalyst: data.stats.catalyst,
                momentum: data.stats.momentum,
                rebound: data.stats.rebound || 0,
                none: data.stats.none,
              };

        setLocalCounts(forFilterBar);

        // Sidebar Scanner badge = rows in the main signals table
        setGlobalCounts((prev) => ({
          ...prev,
          all: filter === 'all' ? fromRows.all : (data.stats?.total ?? fromRows.all),
        }));
        currentDelay.current = 30000;
        if (showScan) setScanPulse((n) => n + 1);
      } catch (err) {
        if (!isMounted) return;
        setError("Couldn't load signals");
        currentDelay.current = Math.min(currentDelay.current * 2, 240000);
      } finally {
        if (isMounted) setLoading(false);
        if (showScan) {
          const elapsed = Date.now() - scanStartedAt;
          const holdMs = Math.max(0, 1200 - elapsed);
          clearTimeout(scanHoldId);
          scanHoldId = setTimeout(() => {
            if (isMounted) setIsScanning(false);
          }, holdMs);
        }
      }
    };

    fetchData(signals.length > 0);

    const scheduleNextRefresh = () => {
      timeoutId = setTimeout(async () => {
        await fetchData(true);
        scheduleNextRefresh();
      }, currentDelay.current);
    };

    scheduleNextRefresh();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      clearTimeout(scanHoldId);
    };
  }, [filter, sort, retryTrigger, setGlobalCounts]); // הוספת התלות של setGlobalCounts ליתר ביטחון

  return (
    <>
      {/* Outside space-y so mounting never shifts page layout */}
      {anyScanning && (
        <>
          <div
            className="pointer-events-none fixed inset-x-0 top-[92px] md:top-[96px] bottom-0 md:left-64 z-[25] overflow-hidden"
            aria-hidden="true"
          >
            <span className="absolute inset-0 bg-[#4f8ff8]/[0.015] dark:bg-[#4f8ff8]/[0.025]" />
            <span className="absolute inset-y-0 left-0 w-[32%] bg-gradient-to-r from-[#4f8ff8]/[0.06] via-[#4f8ff8]/[0.05] to-transparent animate-scan-sweep" />
            <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-[#4f8ff8]/20 via-[#4f8ff8]/35 to-[#4f8ff8]/20 shadow-[0_0_8px_1px_rgba(79,143,248,0.16)] animate-scan-beam-viewport" />
          </div>
          <div className="pointer-events-none fixed top-[100px] md:top-[104px] left-0 md:left-64 right-0 z-[26] flex justify-center px-4">
            <div className="relative overflow-hidden flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/85 dark:bg-[#0e1624]/88 border border-[#4f8ff8]/20 dark:border-[#4f8ff8]/25 shadow-[0_4px_16px_rgba(79,143,248,0.10)] backdrop-blur-md">
              <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-[#4f8ff8]/10 to-transparent animate-scan-sweep" aria-hidden="true" />
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#4f8ff8]/60 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#4f8ff8]/80" />
              </span>
              <span className="relative text-[10px] font-mono font-medium tracking-[0.12em] uppercase text-[#4f8ff8]/90 dark:text-[#4f8ff8]/85">
                Scanning · updating tables
              </span>
            </div>
          </div>
        </>
      )}

    <div className={`space-y-6 ${anyScanning ? 'scanner-page-scanning' : ''}`}>
      {/* ── Hero banner ── */}
      <div className={`relative rounded-2xl overflow-hidden border bg-gradient-to-br from-blue-50 via-violet-50/60 to-white dark:from-[#0e1624] dark:via-[#0f1a30] dark:to-[#080c14] p-6 transition-[border-color,box-shadow] duration-500 ${
        anyScanning
          ? 'border-[#4f8ff8]/30 dark:border-[#4f8ff8]/25 shadow-[0_0_0_1px_rgba(79,143,248,0.06),0_0_20px_rgba(79,143,248,0.08)]'
          : 'border-blue-100 dark:border-[#1d2d47]'
      }`}>
        <div className="pointer-events-none absolute -top-8 -right-8 w-48 h-48 rounded-full bg-gradient-to-br from-[#4f8ff8] to-[#6366f1] opacity-[0.08] dark:opacity-[0.12] blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-32 h-32 rounded-full bg-gradient-to-t from-[#10d98e] to-transparent opacity-[0.07] dark:opacity-[0.10] blur-2xl" />
        {anyScanning && (
          <span
            className="pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-[#4f8ff8]/[0.06] to-transparent blur-sm animate-scan-sweep"
            aria-hidden="true"
          />
        )}

        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <h1 className="text-[22px] font-bold tracking-tight bg-gradient-to-r from-[#4f8ff8] via-[#818cf8] to-[#a78bfa] bg-clip-text text-transparent">
                Scanner
              </h1>
            </div>
            <p className="text-[13px] text-gray-500 dark:text-[#a3b6cc] ml-0.5">
              Tickers passing momentum rules · change ≥ 3% · rel vol ≥ 2×
            </p>
          </div>

          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-[#0e1624] border shadow-sm transition-colors duration-300 ${
            anyScanning
              ? 'border-[#4f8ff8]/40 dark:border-[#4f8ff8]/45'
              : 'border-emerald-200 dark:border-[#10d98e]/30'
          }`}>
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                anyScanning ? 'bg-[#4f8ff8]' : 'bg-emerald-400'
              }`} />
              <span className={`relative inline-flex rounded-full h-2 w-2 ${
                anyScanning ? 'bg-[#4f8ff8]' : 'bg-emerald-500 dark:bg-[#10d98e]'
              }`} />
            </span>
            <span className={`text-[11px] font-semibold font-mono tracking-wide transition-colors duration-300 ${
              anyScanning
                ? 'text-[#4f8ff8]'
                : 'text-emerald-700 dark:text-[#10d98e]'
            }`}>
              {anyScanning ? 'SCANNING' : 'LIVE'}
            </span>
          </div>
        </div>

        {!loading && (
          <div className="relative flex items-center gap-2.5 mt-4 flex-wrap">
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/80 dark:bg-[#0e1624]/80 border border-gray-100 dark:border-[#1d2d47] shadow-sm">
              <div>
                <div className="text-[18px] font-bold font-mono tabular-nums text-gray-900 dark:text-[#f1f5fb] leading-none">{counts.all}</div>
                <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">Total</div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#4f8ff8]/8 dark:bg-[#4f8ff8]/10 border border-[#4f8ff8]/20 dark:border-[#4f8ff8]/25">
              <span className="w-2 h-2 rounded-full bg-[#4f8ff8] shrink-0" />
              <div>
                <div className="text-[18px] font-bold font-mono tabular-nums text-[#4f8ff8] leading-none">{counts.catalyst}</div>
                <div className="text-[10px] text-[#4f8ff8]/70 mt-0.5 uppercase tracking-wide font-semibold">Catalyst</div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-amber-400/8 dark:bg-amber-400/10 border border-amber-300/30 dark:border-amber-400/25">
              <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
              <div>
                <div className="text-[18px] font-bold font-mono tabular-nums text-amber-600 dark:text-amber-400 leading-none">{counts.momentum}</div>
                <div className="text-[10px] text-amber-500/70 dark:text-amber-400/70 mt-0.5 uppercase tracking-wide font-semibold">Momentum</div>
              </div>
            </div>
            {/* כרטיסיית Rebound עליונה */}
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-emerald-400/8 dark:bg-emerald-400/10 border border-emerald-300/30 dark:border-emerald-400/25">
              <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
              <div>
                <div className="text-[18px] font-bold font-mono tabular-nums text-emerald-600 dark:text-[#10d98e] leading-none">{counts.rebound}</div>
                <div className="text-[10px] text-emerald-500/70 dark:text-[#10d98e]/70 mt-0.5 uppercase tracking-wide font-semibold">Rebound</div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-gray-100/80 dark:bg-[#1d2d47]/30 border border-gray-200 dark:border-[#1d2d47]">
              <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-[#a3b6cc] shrink-0" />
              <div>
                <div className="text-[18px] font-bold font-mono tabular-nums text-gray-600 dark:text-[#c2d0e0] leading-none">{counts.none}</div>
                <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">Unconfirmed</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <FilterBar
        onFilterChange={setFilter}
        onSortChange={setSort}
        currentFilter={filter}
        currentSort={sort}
        counts={counts}
      />

{error ? (
  <div className="rounded-2xl border border-red-200 dark:border-[#f5476a]/20 bg-red-50 dark:bg-[#f5476a]/5 py-12 flex flex-col items-center gap-3">
    <svg className="w-8 h-8 text-red-400 dark:text-[#f5476a]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg> {/* ◄ התיקון כאן: תגית סגירה תקינה */}
    <p className="text-[14px] font-medium text-red-600 dark:text-[#f5476a]">{error}</p>
    <button 
      onClick={() => setRetryTrigger(prev => prev + 1)}
      className="mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-[13px] font-medium transition-colors shadow-sm"
    >
      Retry
    </button>
  </div>
) : (
  <SignalsTable 
     title="All Signals"
     subtitle="Matching your current filters"
     topStripGradient="from-[#4f8ff8] via-[#818cf8] to-[#10d98e]"
     headerBgClass="bg-gray-50/60 dark:bg-[#080c14]/50"
     emptyMessage="No signals right now."
     signals={signals}
     isLoading={loading}
     isScanning={anyScanning}
     scanPulse={scanPulse}
  />
)}

      <div id="gainers" className="scroll-mt-[108px] md:scroll-mt-[112px]">
        <h2 className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 dark:text-[#c2d0e0] mb-4">
          Top Gainers
        </h2>
        <TopGainersSection
          pageScanning={anyScanning}
          scanPulse={scanPulse}
          onScanningChange={setGainersScanning}
          onCountChange={(count) => setGlobalCounts(prev => ({ ...prev, gainers: count }))}
        />
      </div>

      <div id="losers" className="scroll-mt-[108px] md:scroll-mt-[112px]">
        <h2 className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 dark:text-[#c2d0e0] mb-4">
          Top Losers
        </h2>
        <TopLosersSection
          pageScanning={anyScanning}
          scanPulse={scanPulse}
          onScanningChange={setLosersScanning}
          onCountChange={(count) => setGlobalCounts(prev => ({ ...prev, losers: count }))}
        />
      </div>
    </div>
    </>
  );
}